import React, { useEffect, useMemo } from 'react'
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import { STAGE_LIMIT } from '../../../helpers/constants'; // Assuming same limit or generic limit
import TablePaginationActions from '../TablePaginationActions';

const columns = [
  { id: 'process_name', label: 'Process Name' },
  { id: 'company_name', label: 'Company Name' },
  { id: 'stage_name', label: 'Stage Name' },
  { id: 'sidebar_menu_id', label: 'Sidebar Menu Name' },
  { id: 'display_order', label: 'Display Order' },
  { id: 'is_active', label: 'Status' },
  { id: 'action', label: 'Actions' }
];

function AssignProcessTable(props) {
  const { is_loading,
    total_count,
    is_modal_open,
    handleUpdateModal,
    handleActivateDeactivate,
    data,
    setPage,
    page
  } = props;

  // Group data by Process and Company
  const groupedData = useMemo(() => {
    if (!data) return {};
    return data.reduce((acc, row) => {
      // Create a unique key based on process and company
      // Use IDs if available to be safer, fall back to names
      // Looking at data structure implied by original code: row.process_name, row.company_name
      const key = `${row.process_name}-${row.company_name}`;
      if (!acc[key]) {
        acc[key] = {
          process_name: row.process_name,
          company_name: row.company_name,
          assign_process_name: row.assign_process_name, // fallback
          items: []
        };
      }
      acc[key].items.push(row);
      return acc;
    }, {});
  }, [data]);

  const groupedRows = Object.values(groupedData);

  const rowsPerPage = STAGE_LIMIT
  const handleStatusChange = async (row) => {
    let newStatus = row.is_active ? 'dc' : 'ac';
    await handleActivateDeactivate({ assign_process_id: row.id, updated_data: newStatus });
  }

  const handleModal = async (c_action, assign_process_id) => {
    await handleUpdateModal(!is_modal_open, c_action, assign_process_id);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const cellStyle = {
    height: '40px',
    display: 'flex',
    alignItems: 'center'
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer
        sx={{
          maxHeight: { xl: 800, md: 440 },
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: "4px",
          },
        }}
      >
        <Table stickyHeader aria-label="sticky table"
          sx={{
            "& .MuiTableCell-stickyHeader": {
              backgroundColor: "#f5f5f5",
              fontWeight: "bold",
              zIndex: 2,
            },
          }}
        >
          <TableHead>
            <TableRow sx={{
              '& .MuiTableCell-root:not(:first-of-type)': {
                textAlign: 'left',
              }
            }}>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#eeeeee70",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {
              groupedRows && groupedRows.length > 0 ?
                groupedRows
                  .map((group, idx) => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={idx} sx={{ verticalAlign: 'top' }}>
                        {/* Process Name */}
                        <TableCell align="left" sx={{ verticalAlign: 'top' }}>
                          <Box sx={cellStyle}>
                            <Tooltip title={group.process_name || group.assign_process_name || "-"} arrow>
                              {group.process_name ? group.process_name : group.assign_process_name ? group.assign_process_name : '-'}
                            </Tooltip>
                          </Box>
                        </TableCell>

                        {/* Company Name */}
                        <TableCell align="left" sx={{ verticalAlign: 'top' }}>
                          <Box sx={cellStyle}>
                            <Tooltip title={group.company_name || group.assign_process_name || "-"} arrow>
                              {group.company_name ? group.company_name : group.assign_process_name ? group.assign_process_name : '-'}
                            </Tooltip>
                          </Box>
                        </TableCell>

                        {/* Stage Name List */}
                        <TableCell align="left" sx={{ padding: '16px 16px 16px 0px' }}>
                          <Stack spacing={0}>
                            {group.items.map((row, i) => (
                              <Box key={i} sx={{ ...cellStyle, paddingLeft: '16px' }}>
                                <Tooltip title={row.stage_name || row.assign_process_name || "-"} arrow>
                                  <span>{row.stage_name ? row.stage_name : row.assign_process_name ? row.assign_process_name : '-'}</span>
                                </Tooltip>
                              </Box>
                            ))}
                          </Stack>
                        </TableCell>

                        {/* Sidebar Menu Name List */}
                        <TableCell align="left" sx={{ padding: '16px 16px 16px 0px' }}>
                          <Stack spacing={0}>
                            {group.items.map((row, i) => (
                              <Box key={i} sx={{ ...cellStyle, paddingLeft: '16px' }}>
                                <Tooltip title={row.sidebar_menu_name || "-"} arrow>
                                  <span>{row.sidebar_menu_name ? row.sidebar_menu_name : '-'}</span>
                                </Tooltip>
                              </Box>
                            ))}
                          </Stack>
                        </TableCell>

                        {/* Display Order List */}
                        <TableCell align="left" sx={{ padding: '16px 16px 16px 0px' }}>
                          <Stack spacing={0}>
                            {group.items.map((row, i) => (
                              <Box key={i} sx={{ ...cellStyle, paddingLeft: '16px' }}>
                                <Tooltip title={row.display_order || "-"} arrow>
                                  <span>{row.display_order ? row.display_order : '-'}</span>
                                </Tooltip>
                              </Box>
                            ))}
                          </Stack>
                        </TableCell>


                        {/* Status List */}
                        <TableCell align="left" sx={{ padding: '16px 16px 16px 0px' }}>
                          <Stack spacing={0}>
                            {group.items.map((row, i) => (
                              <Box key={i} sx={{ ...cellStyle, paddingLeft: '16px' }}>
                                <Tooltip title={row.is_active ? 'Active' : 'Inactive' || "-"} arrow>
                                  <span>{row.is_active ? 'Active' : 'Inactive'}</span>
                                </Tooltip>
                              </Box>
                            ))}
                          </Stack>
                        </TableCell>

                        {/* Actions List */}
                        <TableCell align="left" sx={{ padding: '16px 16px 16px 0px' }}>
                          <Stack spacing={0}>
                            {group.items.map((row, i) => (
                              <Box key={i} sx={{ ...cellStyle, paddingLeft: '16px' }}>
                                <Stack direction="row" spacing={3} alignItems="center">
                                  <Tooltip title="Edit" arrow>
                                    <IconButton size="small" type="button" aria-label="edit" color='primary' onClick={() => handleModal("UPDATE", row.id)}>
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '1.0rem' }}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                                      </svg>
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={row.is_active ? 'Active' : 'Inactive'} arrow>
                                    <IconButton
                                      size="small"
                                      type="button"
                                      aria-label="toggle-status"
                                      color="primary"
                                      onClick={() => handleStatusChange(row)}
                                    >
                                      {row.is_active ? <Visibility sx={{ fontSize: '1.2rem' }} /> : <VisibilityOff sx={{ fontSize: '1.2rem' }} />}
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }) :
                !is_loading &&
                  (!data || data.length === 0) ? (
                  <TableRow>
                    <TableCell align='center' colSpan={7}>
                      No Record Found
                    </TableCell>
                  </TableRow>) :
                  (<TableRow>
                    <TableCell align='center' colSpan={7}>
                      loading....
                    </TableCell>
                  </TableRow>
                  )
            }
          </TableBody>
          {data && data.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={7} sx={{ padding: 0, borderBottom: 'none' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <TablePagination
                      component="div"
                      count={total_count}
                      page={page}
                      onPageChange={handlePageChange}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[]}
                      ActionsComponent={TablePaginationActions}
                      sx={{
                        border: 'none',
                        '& .MuiTablePagination-toolbar': {
                          paddingRight: 2 // Ensure some padding on the right
                        }
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default AssignProcessTable